import type { TypeFormFieldElement } from '../../types/form';

import addClass from '../../general/class/addClass';
import removeClass from '../../general/class/removeClass';
import toMessageString from '../../general/toMessageString';

import getFormFields from './utils/getFormFields';
import getRequiredMessage from './utils/getRequiredMessage';
import getValidationName from './utils/getValidationName';
import getVisibleFieldForHidden from './utils/getVisibleFieldForHidden';
import hasValidationName from './utils/hasValidationName';
import shouldValidateField from './utils/shouldValidateField';

// ----------------------------------------------------------------------------

export type TypeDataConfig = {
    validFields?: Record<string, string | string[]>;
    invalidFields?: Record<string, string | string[]>;
    exceptedFields?: string[];
    excludeTypes?: string[];
    showValid?: boolean;
};

// ----------------------------------------------------------------------------

function validateForm(form: HTMLFormElement, data: TypeDataConfig = {}): boolean {
    const elements = getFormFields(form);

    let isFormValid = true;

    for (const elField of elements) {
        const name = getValidationName(elField);

        if (!shouldValidateField(elField, data, name)) {
            continue;
        }

        const isFieldValid = validateField(elField, data, name);

        if (!isFieldValid) {
            isFormValid = false;
        }

        const elTargetField = getVisibleFieldForHidden(elField);

        if (elTargetField) {
            const isTargetFieldValid = validateField(elTargetField, data, name);

            if (!isTargetFieldValid) {
                isFormValid = false;
            }
        }
    }

    return isFormValid;
}

function validateField(field: TypeFormFieldElement, data: TypeDataConfig = {}, validationName?: string): boolean {
    const name = validationName ?? getValidationName(field);

    if (!name) {
        return true;
    }

    const invalidFeedbackMessage = data.invalidFields?.[name];
    const invalidMessageText = toMessageString(invalidFeedbackMessage);

    field.setCustomValidity('');

    const requiredMessageText = invalidMessageText.length === 0 ? getRequiredMessage(field, name) : '';
    const isInvalid = invalidMessageText.length > 0 || requiredMessageText.length > 0;

    if (isInvalid) {
        addClass(field, 'is-invalid');
        removeClass(field, 'is-valid');

        const validationMessage = invalidMessageText.length > 0 ? invalidMessageText : requiredMessageText;

        field.setCustomValidity(validationMessage);

        updateFeedback(field, '.invalid-feedback', invalidFeedbackMessage ?? validationMessage);
        updateFeedback(field, '.valid-feedback', '');

        return false;
    }

    const showValid = data.showValid ?? false;

    if (!showValid) {
        removeClass(field, 'is-valid');
        removeClass(field, 'is-invalid');

        updateFeedback(field, '.valid-feedback', '');
        updateFeedback(field, '.invalid-feedback', '');

        return true;
    }

    const validFeedbackMessage = data.validFields?.[name];
    const validMessageText = toMessageString(validFeedbackMessage);

    if (showValid && validMessageText.length > 0) {
        addClass(field, 'is-valid');
        removeClass(field, 'is-invalid');

        updateFeedback(field, '.valid-feedback', validFeedbackMessage ?? validMessageText);
        updateFeedback(field, '.invalid-feedback', '');
    } else {
        removeClass(field, 'is-valid');
        removeClass(field, 'is-invalid');

        updateFeedback(field, '.valid-feedback', '');
        updateFeedback(field, '.invalid-feedback', '');
    }

    return true;
}

// ----------------------------------------------------------------------------

function clearValidation(form: HTMLFormElement): void {
    const elements = getFormFields(form);

    for (const elField of elements) {
        if (!hasValidationName(elField)) {
            continue;
        }

        elField.setCustomValidity('');

        clearFieldValidation(elField);
        clearFeedback(elField);

        const elTargetField = getVisibleFieldForHidden(elField);

        if (elTargetField) {
            elTargetField.setCustomValidity('');

            clearFieldValidation(elTargetField);
            clearFeedback(elTargetField);
        }
    }
}

function clearFieldValidation(field: TypeFormFieldElement): void {
    removeClass(field, 'is-invalid');
    removeClass(field, 'is-valid');
}

// ----------------------------------------------------------------------------

function updateFeedback(element: HTMLElement, selector: string, message: string | string[]): void {
    const elFeedback = element.parentElement?.querySelector<HTMLElement>(selector);

    if (!elFeedback) {
        return;
    }

    elFeedback.innerHTML = '';

    //
    if (Array.isArray(message)) {
        if (message.length === 0) {
            return;
        }

        if (message.length === 1) {
            elFeedback.textContent = message[0];
            return;
        }

        const ul = document.createElement('ul');

        for (const msg of message) {
            const li = document.createElement('li');
            li.textContent = msg;
            ul.appendChild(li);
        }

        elFeedback.appendChild(ul);
    }
    //
    else if (typeof message === 'string') {
        if (message.trim().length === 0) {
            return;
        }

        const lines = message.split('\n');

        for (const line of lines) {
            const div = document.createElement('div');
            div.textContent = line;
            elFeedback.appendChild(div);
        }
    }
}

function clearFeedback(field: TypeFormFieldElement): void {
    updateFeedback(field, '.invalid-feedback', '');
    updateFeedback(field, '.valid-feedback', '');
}

// ----------------------------------------------------------------------------

function validateTooltip(form: HTMLFormElement, data: TypeDataConfig = {}): void {
    const elements = getFormFields(form);

    for (const elField of elements) {
        const name = getValidationName(elField);

        if (!shouldValidateField(elField, data, name)) {
            continue;
        }

        validateField(elField, data, name);

        const elTargetField = getVisibleFieldForHidden(elField);

        if (elTargetField) {
            validateField(elTargetField, data, name);
        }
    }
}

// ----------------------------------------------------------------------------

export { validateForm, validateTooltip, clearValidation };
